import React, { useState, useCallback } from 'react';
import {
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Flex,
    HStack,
    ButtonGroup,
    DrawerBody,
    VStack,
    Button,
    InlineBanner,
    Text,
    Box,
    Input,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useHoldEventDrawerData } from './use-hold-event-drawer-data';
import { GroupedVirtuoso } from 'react-virtuoso';
import { EventSummary } from './components/event-summary';
import { UnitsSummary } from '../manage-units/common/units-summary';
import { convertHoldEventUnitViewToPillUnit } from './convert-hold-event-unit-view-to-pill-unit';
import { HoldEventUnitGroupEnum, HoldEventUnitGroupEnumType } from '@mark43/cad-api';
import { GroupHeader } from '../manage-units/common/group-header';
import { FilterPopover } from '../manage-units/common/filter-popover/filter-popover';
import { SortMenu } from '../manage-units/common/sort-menu';
import { BannersDisplay } from '../manage-units/common/banners-display';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { UnitTile } from '../common/components/unit-tile';
import { TileUnit } from '../common/types';
import { useKeyboardNavigation } from '../common/hooks/use-tile-keyboard-navigation';
import { noop } from 'lodash';
import { unitCallSignSortComparator } from './utils';
import { useSubmitFormOnEnter } from '../common/hooks/use-submit-form-on-enter';

export const HoldEventDrawer = observer(function HoldEventDrawer({
    eventId,
}: {
    eventId: number;
}): React.ReactElement {
    const drawerControls = useDrawerControls();
    const closeDrawer = React.useMemo(() => {
        return () => {
            drawerControls.close();
        };
    }, [drawerControls]);

    const {
        store,
        event,
        initiallySelectedUnitIds,
        initiallySelectedUnits,
        selectedUnits,
        unheldUnits,
        toggleSelectUnit,
        selectedUnitIds,
        debouncedSetQuery,
        selectedFilters,
        hasSelectedFilters,
        setSelectedFilters,
        sortingParam,
        setSortDirection,
        sortDirection,
        setSortingParam,
        submit,
        isEventQueryLoading,
        isUnitsQueryLoading,
        isQueryError,
        isMutationLoading,
        isMutationError,
    } = useHoldEventDrawerData(eventId);

    const rows = store.data.list.slice();
    const groupCounts = store.data.groupCounts.slice();
    const groupHeaders = store.data.groupHeaders.slice();
    const [drawerContentElement, setDrawerContentElement] = useState<HTMLElement | null>(null);

    const canFocusPrimary = useCallback(() => {
        return false;
    }, []);

    const keyboardTileNavigation = useKeyboardNavigation({
        store,
        onSelectItem: toggleSelectUnit,
        onSetPrimaryItem: noop,
        element: drawerContentElement,
        canFocusPrimary,
    });

    const toggleSelectTileUnit = (tileUnit: TileUnit) => {
        const selectedUnit = store.data.list.find((unit) => unit.id === tileUnit.id);
        if (selectedUnit) {
            toggleSelectUnit(selectedUnit);
        }
    };

    const renderItemContent = (index: number): React.ReactElement => {
        const unit = rows[index];

        return (
            <Box paddingRight="16px">
                <UnitTile
                    unit={unit}
                    toggleSelect={toggleSelectTileUnit}
                    isSelected={selectedUnitIds.includes(unit.id)}
                    key={index}
                    hasPrimary={false}
                    isFocused={index === keyboardTileNavigation.currentIndex}
                    isFocusingLeft={keyboardTileNavigation.isFocusingLeft}
                    isFocusingRight={keyboardTileNavigation.isFocusingRight}
                />
            </Box>
        );
    };

    function renderGroupContent(index: number): React.ReactElement {
        return (
            <GroupHeader
                displayName={
                    HoldEventUnitGroupEnum[groupHeaders[index] as HoldEventUnitGroupEnumType]
                        .displayName
                }
                count={groupCounts[index]}
            />
        );
    }

    useSubmitFormOnEnter({
        element: drawerContentElement,
        onSubmit: submit,
    });

    return (
        <Drawer variant="overlay" isOpen={true} size="lg" onClose={closeDrawer}>
            <DrawerContent>
                <Flex height="100%" direction="column" ref={setDrawerContentElement}>
                    {isEventQueryLoading ? (
                        <>
                            <DrawerHeader>
                                <Box mt="1" mb="3">
                                    <Text variant="headingMd">Hold Event</Text>
                                </Box>
                            </DrawerHeader>
                            <DrawerBody>
                                <LoadingIndicator />
                            </DrawerBody>
                        </>
                    ) : (
                        <>
                            <DrawerHeader>
                                <Box mt="1" mb="3">
                                    <Text variant="headingMd">
                                        Hold Event {event ? event.eventNumber : ''}
                                    </Text>
                                </Box>
                                {event && (
                                    <>
                                        <VStack gap="8px" align="left">
                                            <EventSummary event={event} />
                                            <Box style={{ marginTop: 0 }}>
                                                <UnitsSummary
                                                    label="Already Held"
                                                    units={initiallySelectedUnits
                                                        .sort(unitCallSignSortComparator)
                                                        .map(convertHoldEventUnitViewToPillUnit)}
                                                    ghostUnitIds={unheldUnits.map(
                                                        (unit) => unit.id
                                                    )}
                                                />
                                                <UnitsSummary
                                                    label="To Hold"
                                                    units={selectedUnits
                                                        .filter((unit) => {
                                                            return !initiallySelectedUnitIds.includes(
                                                                unit.id
                                                            );
                                                        })
                                                        .sort(unitCallSignSortComparator)
                                                        .map(convertHoldEventUnitViewToPillUnit)}
                                                />
                                            </Box>
                                        </VStack>
                                        <Flex my="3" flexDirection="column" gap="2px">
                                            <Input
                                                data-testid="hold-event-text-filter-input"
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    debouncedSetQuery(e.currentTarget.value);
                                                }}
                                                leftIcon="Search"
                                                isClearable={true}
                                                autoFocus={true}
                                                size="sm"
                                            ></Input>
                                            <Text color="tertiary" variant="bodyMd">
                                                Search by Unit Call Sign, Location, Status, Type, or
                                                Skill
                                            </Text>
                                        </Flex>
                                        <HStack gap="1">
                                            <FilterPopover
                                                selectedFilters={selectedFilters}
                                                setSelectedFilters={setSelectedFilters}
                                                hasSelectedFilters={hasSelectedFilters}
                                            />
                                            <SortMenu
                                                sortingParam={sortingParam}
                                                sortDirection={sortDirection}
                                                onChangeSortDirection={(sortOrder) => {
                                                    setSortDirection(sortOrder);
                                                }}
                                                onChangeSortingParam={(sortingParam) => {
                                                    setSortingParam(sortingParam);
                                                }}
                                            />
                                        </HStack>
                                    </>
                                )}
                                {isMutationError && (
                                    <BannersDisplay
                                        banners={[
                                            {
                                                status: 'error',
                                                title: 'Error',
                                                description: errorOccurredWhile('holding event'),
                                            },
                                        ]}
                                    />
                                )}
                            </DrawerHeader>
                            <DrawerBody
                                style={{ paddingTop: '8px', paddingRight: 0, paddingBottom: 0 }}
                            >
                                {isQueryError && (
                                    <Flex
                                        w="100%"
                                        h="100%"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <InlineBanner
                                            status="error"
                                            description="An error occurred while loading data"
                                        />
                                    </Flex>
                                )}
                                {isUnitsQueryLoading ? (
                                    <LoadingIndicator />
                                ) : (
                                    <GroupedVirtuoso
                                        ref={keyboardTileNavigation.virtuosoRef}
                                        groupCounts={groupCounts}
                                        groupContent={renderGroupContent}
                                        style={{ height: '100%', width: '100%' }}
                                        rows={rows.length}
                                        overscan={200}
                                        itemContent={renderItemContent}
                                    />
                                )}
                            </DrawerBody>
                        </>
                    )}
                    <DrawerFooter>
                        <ButtonGroup>
                            <Button
                                variant="ghost"
                                onClick={closeDrawer}
                                isDisabled={isMutationLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="solid"
                                isDisabled={isEventQueryLoading || isUnitsQueryLoading}
                                isLoading={isMutationLoading}
                                onClick={() => {
                                    submit();
                                }}
                            >
                                Save
                            </Button>
                        </ButtonGroup>
                    </DrawerFooter>
                </Flex>
            </DrawerContent>
        </Drawer>
    );
});

type HoldEventDrawerType = typeof HoldEventDrawer;
export type { HoldEventDrawerType };
